import { useState, useContext, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputSelect,
  Label,
  Button,
  InputDate,
  Switch,
  IconButton,
  PersonAvatar,
} from '@teo/components';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { languages } from '@/constants/languages';
import { PaginationMultiSelect } from '@/components/PaginationMultiSelect';
import { PaginationSingleSelect } from '@/components/PaginationSingleSelect';
import { parseDate } from '@/util';
import { useGet } from '@/query/fetchHooks';
import { CrossFilled } from '@teo/components/icons';
import DownloadReportModal from '../DownloadReportModal';
import ReportLpDataTable from '@/components/ReportDataTable/ReportLpDataTable';

const LP_OVERVIEW_VALUE = 'lpOverview';
const LP_OVERVIEW_NO_COMPETENCES_VALUE = 'lpOverviewNoCompetences';

const LP_OVERVIEW_COMPACT_VALUE = 'overviewLp';
const LP_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE = 'overviewLpNoCompetences';

const reportTypeList = [
  {
    value: LP_OVERVIEW_NO_COMPETENCES_VALUE,
    label: 'report.lpOverviewDetailed',
  },
  {
    value: LP_OVERVIEW_VALUE,
    label: 'report.courseOverviewDetailedCompetences',
  },
  {
    value: LP_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE,
    label: 'report.lpOverview',
  },
  {
    value: LP_OVERVIEW_COMPACT_VALUE,
    label: 'report.courseOverviewCompetences',
  },
  {
    value: 'lpCsv',
    label: 'report.lpCsv',
  },
  {
    value: 'lpXlsx',
    label: 'report.lpXlsx',
  },
  {
    value: 'lpStudentOverview',
    label: 'report.student_overview',
  },
  {
    value: 'lpdetailsCsv',
    label: 'report.lpdetailsCsv',
  },
  {
    value: 'lpdetailsXlsx',
    label: 'report.lpdetailsXlsx',
  },
  // {
  //   value: 'certificate',
  //   label: 'report.certificate',
  // },
  // {
  //   value: 'aggregateLessonCsv',
  //   label: 'report.resultsCsv',
  // },
  // {
  //   value: 'aggregateLessonXlsx',
  //   label: 'report.resultsXlsx',
  // },
  // {
  //   value: 'exportResults',
  //   label: 'report.resultsPdf',
  // },
];

function LearningPathReport() {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const [fromDate, setFromDate] = useState();
  const [untilDate, setUntilDate] = useState();
  const [selectType, setSelectType] = useState(reportTypeList[0].value);
  const [allowLanguage, setAllowLanguage] = useState(true);
  const [allowDateRange, setAllowDateRange] = useState(true);
  const [allowMultiLpSelect, setAllowMultiLpSelect] = useState(true);
  const [allLpGroups, setAllLpGroups] = useState(false);
  const [selectedLp, setSelectedLp] = useState([]);
  const [allUsers, setAllUsers] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSelect, setUserSelect] = useState(false);
  const [studentSelect, setStudentSelect] = useState(false);
  const [isOpenDownloadtModal, setIsOpenDownloadtModal] = useState(false);
  const [enableNodeSelect, setEnableNodeSelect] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState([]);

  const reportState = {
    lpReport: [],
    usersReport: [],
    fromDate,
    untilDate,
    reportType: {
      label: reportTypeList[0].label,
      value: reportTypeList[0].value,
    },
    reportEmail: true,
    lang: languages?.find((option) => option.value === auth?.user?.lang),
    noCompetences: reportTypeList[0].value === LP_OVERVIEW_NO_COMPETENCES_VALUE,
  };

  const [stateReport, updateStateReport] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    reportState
  );

  const isNoCompetencesReport =
    stateReport.reportType?.value === LP_OVERVIEW_NO_COMPETENCES_VALUE ||
    stateReport.reportType?.value === LP_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE;

  const getDownloadReportType = (value) => {
    if (value === LP_OVERVIEW_NO_COMPETENCES_VALUE) {
      return LP_OVERVIEW_VALUE;
    }

    if (value === LP_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE) {
      return LP_OVERVIEW_COMPACT_VALUE;
    }

    return value;
  };

  const downloadStateReport = {
    ...stateReport,
    reportType: {
      ...stateReport.reportType,
      value: getDownloadReportType(stateReport.reportType?.value),
    },
    noCompetences: isNoCompetencesReport,
  };

  const getLpId = (lp) => lp?.lpgroupId ?? lp?.id;
  const selectedLpGroup = selectedLp?.[0];
  const selectedCourseId = getLpId(selectedLpGroup);

  const handleAddLp = async (newValue) => {
    const addLp = [...stateReport.lpReport];
    for (const obj2 of newValue) {
      if (obj2?.lpgroupId || obj2?.id) {
        const existingCourses = stateReport.lpReport.find(
          (obj1) =>
            (obj1?.lpgroupId && obj1?.lpgroupId === obj2?.lpgroupId) ||
            (obj1?.id && obj1?.id === obj2?.id)
        );
        if (!existingCourses) {
          addLp.push(obj2);
        }
      }
    }
    updateStateReport({ lpReport: addLp });
    setSelectedLp([]);
  };

  const handleRemove = (courseId) => {
    const removeCourse = stateReport.lpReport?.filter(
      (item) => getLpId(item) !== courseId
    );
    updateStateReport({ lpReport: removeCourse });
  };

  const handleAddUsers = async (newValue) => {
    const addUser = [...(stateReport.usersReport || [])];
    for (const obj2 of newValue) {
      if (obj2?.id) {
        const existingTeam = (stateReport.usersReport || []).find(
          (obj1) => obj1?.id && obj1?.id === obj2?.id
        );
        if (!existingTeam) {
          addUser.push(obj2);
        }
      }
    }
    updateStateReport({ usersReport: addUser });
    setSelectedUsers([]);
  };

  const handleRemoveUsers = (id) => {
    const removeUser = stateReport.usersReport?.filter(
      (item) => item.id !== id
    );
    updateStateReport({ usersReport: removeUser });
  };

  useEffect(() => {
    if (allUsers) {
      updateStateReport({ usersReport: [] });
    }
  }, [allUsers]);

  useEffect(() => {
    //defaults that works for the most reports
    setAllowMultiLpSelect(true);
    setAllowDateRange(true);
    setUserSelect(false);
    setStudentSelect(false);
    setAllUsers(true);
    setEnableNodeSelect(false);

    //reset data
    setSelectedUsers([]);
    setSelectedLp([]);

    //exceptions
    if (
      selectType == LP_OVERVIEW_VALUE ||
      selectType == LP_OVERVIEW_NO_COMPETENCES_VALUE ||
      selectType == LP_OVERVIEW_COMPACT_VALUE ||
      selectType == LP_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE ||
      selectType == 'overviewCourse' ||
      selectType == 'lpStudentOverview' ||
      selectType == 'certificate' ||
      selectType == 'aggregateLessonCsv' ||
      selectType == 'aggregateLessonXlsx'
    ) {
      setUserSelect(true);
    }
    if (selectType == 'exportResults') {
      setAllowMultiLpSelect(false);
      setAllowDateRange(false);
      setStudentSelect(true);
    }
    if (
      selectType == 'aggregateLessonCsv' ||
      selectType == 'aggregateLessonXlsx'
    ) {
      setAllowMultiLpSelect(false);
      setAllowDateRange(false);
      setEnableNodeSelect(true);
    }
    if (selectType.endsWith('Csv') || selectType.endsWith('Xlsx')) {
      setAllowLanguage(false);
    }
  }, [selectType]);

  return (
    <div className="gap- flex flex-col">
      <div className="mt-6 rounded-lg border border-grey-02 p-6">
        <div className="flex max-w-xl flex-col">
          <p className="pb-2 text-base font-semibold text-black">
            {t('pages.report_page.teams_tab.title_1')}
          </p>
          <div className="mb-1 mt-4 text-sm font-semibold text-black">
            <p className="mb-1">
              {t('pages.report_page.teams_tab.type_of_report')}
            </p>
            <div className="flex">
              <span className="w-full">
                <InputSelect
                  value={stateReport.reportType}
                  onChange={(e) => {
                    const noCompetences =
                      e?.value === LP_OVERVIEW_NO_COMPETENCES_VALUE ||
                      e?.value === LP_OVERVIEW_COMPACT_NO_COMPETENCES_VALUE;

                    setSelectType(e?.value);
                    updateStateReport({
                      reportType: e,
                      noCompetences,
                    });
                  }}
                  options={reportTypeList}
                >
                  {(options) => (
                    <span>{options.label && t(options.label)}</span>
                  )}
                </InputSelect>
              </span>
            </div>
          </div>

          <div className="z-[1] mb-1 mt-4 text-sm font-semibold text-black">
            <p className="mb-1">{t(`columns.learningpath`)}</p>
            <div className="relative z-[2] flex">
              <span className="w-full">
                {allowMultiLpSelect ? (
                  <PaginationMultiSelect
                    id="teams-report"
                    value={selectedLp}
                    onChange={(value) => {
                      setSelectedLp(value);
                    }}
                    displayValue={(el) => {
                      if (!el || el?.length === 0) return '';
                      return (
                        ((el?.lpgroupName ? el?.lpgroupName : el?.lpName) ||
                          '') +
                        ` (${parseDate(el?.courseStartDate || el?.createdAt)})`
                      );
                    }}
                    keyFn={(row) => getLpId(row)}
                    fetch={useGet.bind(
                      null,
                      `/memberlpgroupdetails?organisationId=${auth?.user?.activeOrganisationId}`
                    )}
                    defaultSort={[{ id: 'lpgroupName', desc: false }]}
                    render={(row) => {
                      return (
                        <div className="flex justify-between">
                          <span>
                            {row?.lpgroupName ? row?.lpgroupName : row?.lpName}
                          </span>
                          <span>{`${parseDate(
                            row?.courseStartDate || row?.createdAt
                          )}`}</span>
                        </div>
                      );
                    }}
                  />
                ) : (
                  <PaginationSingleSelect
                    id="teams-report"
                    value={selectedLp?.[0]}
                    onChange={(value) => {
                      setSelectedLp([value]);
                    }}
                    keyFn={(row) => getLpId(row)}
                    fetch={useGet.bind(
                      null,
                      `/memberlpgroupdetails?organisationId=${auth?.user?.activeOrganisationId}`
                    )}
                    displayValue={(el) => {
                      if (!el || el?.length === 0) return '';
                      return (
                        ((el?.lpgroupName ? el?.lpgroupName : el?.lpName) ||
                          '') +
                        ` (${parseDate(el?.courseStartDate || el?.createdAt)})`
                      );
                    }}
                    defaultSort={[{ id: 'lpgroupName', desc: false }]}
                    render={(row) => {
                      return (
                        <div className="flex justify-between">
                          <span>
                            {row?.lpgroupName ? row?.lpgroupName : row?.lpName}
                          </span>
                          <span>{`${parseDate(
                            row?.courseStartDate || row?.createdAt
                          )}`}</span>
                        </div>
                      );
                    }}
                  />
                )}
              </span>
              {allowMultiLpSelect && (
                <Button
                  className="ml-3 h-[36px] text-base font-medium"
                  variant="primary"
                  disabled={selectedLp.length === 0}
                  onClick={async () => {
                    handleAddLp(selectedLp);
                  }}
                >
                  {t('button.add_btn')}
                </Button>
              )}
            </div>
            {stateReport?.lpReport?.length > 0 && (
              <div className="mt-6">
                {stateReport?.lpReport?.map((learningpath) => (
                  <div
                    key={getLpId(learningpath) ?? learningpath.username}
                    className="mb-4 flex items-center justify-between"
                  >
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center">
                        <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                          <img
                            src={
                              learningpath?.lpgroupPicture ||
                              learningpath?.lpPicture ||
                              '/assets/images/LearnPath.png'
                            }
                            alt={
                              learningpath?.lpgroupName ||
                              learningpath?.lpName ||
                              ''
                            }
                            className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                          />
                        </div>
                        {learningpath?.lpgroupName || learningpath?.lpName}
                      </div>
                      <div>{parseDate(learningpath?.createdAt)}</div>
                    </div>
                    <div data-testid="daleteItem" className="my-auto ml-6">
                      <IconButton
                        Icon={CrossFilled}
                        variant="ghost"
                        onClick={() => handleRemove(getLpId(learningpath))}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(userSelect || (studentSelect && !!selectedCourseId)) && (
              <>
                <div className="z-[1] mb-1 mt-4 text-sm font-semibold text-black">
                  <p className="mb-1">
                    {t('pages.report_page.user_tab.users')}
                  </p>
                  <div className="mt-2 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
                    <div data-testid="reportEmail">
                      <Switch
                        checked={allUsers}
                        onChange={(isCheck) => setAllUsers(isCheck)}
                      />
                    </div>
                    <div>
                      <h4 className="font-normal">
                        {t('pages.report_page.select_users')}
                      </h4>
                    </div>
                  </div>
                  {!allUsers && (
                    <>
                      <div className="mt-4 flex">
                        <span className="w-full">
                          <PaginationMultiSelect
                            id="teams-report"
                            value={selectedUsers}
                            onChange={(value) => {
                              setSelectedUsers(value);
                            }}
                            keyFn={(row) => row?.id}
                            fetch={useGet.bind(
                              null,
                              `/memberdetails?organisationId=${auth?.user?.activeOrganisationId}`
                            )}
                            defaultSort={[{ id: 'username', desc: false }]}
                            render={(row) => {
                              return row?.username;
                            }}
                          />
                        </span>
                        <Button
                          className="ml-3 h-[36px] text-base font-medium"
                          variant="primary"
                          disabled={selectedUsers.length === 0}
                          onClick={async () => {
                            handleAddUsers(selectedUsers);
                          }}
                        >
                          {t('button.add_btn')}
                        </Button>
                      </div>
                      {stateReport?.usersReport?.length > 0 && (
                        <div className="mt-6">
                          {stateReport?.usersReport?.map((user) => (
                            <div
                              key={user.id == null ? user.username : user.id}
                              className="mb-4 flex items-center justify-between"
                            >
                              <div className="flex items-center">
                                <PersonAvatar
                                  inline
                                  id={user.id}
                                  name={user.username}
                                  imageSrc={user.picture}
                                ></PersonAvatar>
                                <p className="text-[12px] font-medium text-[#231f20] sm:text-sm">
                                  {user.username}
                                </p>
                              </div>
                              <div
                                data-testid="daleteItem"
                                className="my-auto ml-6"
                              >
                                <IconButton
                                  Icon={CrossFilled}
                                  variant="ghost"
                                  onClick={() => handleRemoveUsers(user.id)}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {enableNodeSelect && selectedLp.length > 0 && (
            <div className="mb-1 mt-4 text-sm font-semibold text-black">
              <p className="mb-1">
                {t(`pages.create_library.lesson`)} ({t('forms.optional')})
              </p>
              <div className="flex">
                <span className="w-full">
                  <PaginationSingleSelect
                    id="teams-report"
                    value={selectedNodes?.[0]}
                    onChange={(value) => {
                      setSelectedNodes([value]);
                    }}
                    keyFn={(row) => row?.id}
                    fetch={useGet.bind(
                      null,
                      `/learningpathnodedetails?organisationId=${auth?.user?.activeOrganisationId}&learningpathId=${selectedLpGroup?.learningpathId}&type=course,OR,type=training`
                    )}
                    defaultSort={[{ id: 'name', desc: false }]}
                    render={(row) => {
                      return row?.name;
                    }}
                  />
                </span>
              </div>
            </div>
          )}

          {allowLanguage && (
            <div className="mt-3">
              <Label htmlFor="language">
                {t('pages.profile_account.language')}
              </Label>
              <div id="language">
                <InputSelect
                  value={stateReport.lang}
                  onChange={async (language) => {
                    updateStateReport({ lang: language });
                  }}
                  options={languages}
                >
                  {(options) => t(options.label)}
                </InputSelect>
              </div>
            </div>
          )}
          {allowDateRange && (
            <div className="mt-7 flex flex-col gap-5 pr-2 sm:flex-row lg:pr-0">
              <div className="basis-1/2" data-testid="dateStart">
                <Label htmlFor="dateStart">
                  {t('pages.report_page.teams_tab.from')}
                </Label>
                <InputDate
                  id="dateStart"
                  onChange={(date) => updateStateReport({ fromDate: date })}
                  value={stateReport.fromDate}
                  variant="date"
                />
              </div>
              <div className="basis-1/2" data-testid="dateEnd">
                <Label htmlFor="dateEnd">
                  {t('pages.report_page.teams_tab.until')}
                </Label>
                <InputDate
                  id="dateEnd"
                  onChange={(date) => updateStateReport({ untilDate: date })}
                  value={stateReport.untilDate}
                  variant="date"
                />
              </div>
            </div>
          )}
          <div className="mt-5 flex flex-row items-center gap-4 rounded-lg border border-grey-02 p-4">
            <div data-testid="reportEmail">
              <Switch
                checked={stateReport.reportEmail}
                onChange={(isCheck) =>
                  updateStateReport({ reportEmail: isCheck })
                }
              />
            </div>
            <div>
              <h4 className="pb-2 font-normal">
                {t('pages.report_page.teams_tab.toggle_email_heading')}
              </h4>
              <p className="text-xs text-grey-05">
                {t('pages.report_page.teams_tab.toggle_email_text')}
              </p>
            </div>
          </div>
          <div className="flex pt-6">
            <Button
              onClick={() => {
                // if (!allowMultiLpSelect) {
                // updateStateReport({ lpReport: selectedLp });
                // }
                //  else {
                //   updateStateReport({ selectedLessons: undefined });
                // }
                document.body.style.overflow = 'hidden';
                setIsOpenDownloadtModal(true);
              }}
              disabled={
                stateReport?.lpReport?.length < 1 && !(selectedLp.length > 0)
              }
            >
              {t('pages.report_page.teams_tab.btn_download')}
            </Button>
          </div>
        </div>
      </div>
      <ReportLpDataTable reportTypeList={reportTypeList} />

      {isOpenDownloadtModal && (
        <DownloadReportModal
          report={'learningpath'}
          isOpenDownloadtModal={isOpenDownloadtModal}
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenDownloadtModal(false);
          }}
          setIsOpenDownloadtModal={setIsOpenDownloadtModal}
          stateReport={downloadStateReport}
          user={auth?.user}
          updateStateReport={updateStateReport}
          allSelect={allLpGroups ? undefined : true}
        />
      )}
    </div>
  );
}
export default LearningPathReport;
